import { FC, useCallback, useState } from 'react';
import { Box } from '@mui/material';
import ModalDialog from '../../../common/components/ModalDialog';
import { IssueDetails } from '../types/issues-types';
import AppSpinner from '../../../common/components/AppSpinner';
import SendSlackMessgeFooter from '../components/SendSlackMessgeContent/SendSlackMessgeFooter';
import SendSlackMessgeContent from '../components/SendSlackMessgeContent/SendSlackMessgeContent';
import {
  sendSlackNotification,
  setSelectedChannel,
  setUserComment,
  useSlackStore,
} from '../../../slack/api/slack-api';
import { pushSlackNotificationToRawIssue } from '../../active-issues/store-actions/issue-store-actions';
import { createSlackMessageBlocks } from '../utils/slack-message-utils';
import { useAuth0 } from '@auth0/auth0-react';
import { Nullable } from 'common-types';
import { Channel } from '../../../slack/types/slack-types';

const SendSlackMessageModalContainer: FC<
  React.PropsWithChildren<SendSlackMessageModalContainerProps>
> = ({ open, onClose, selectedIssue }) => {
  const { user } = useAuth0();
  const { loadingSlackChannels, publicChannels, fields, loadingSubmit } =
    useSlackStore();
  const [inputError, setInputError] = useState('');

  const onSelectedChannelChange = useCallback(
    (
      _event: React.SyntheticEvent<Element, Event>,
      value: Nullable<Channel>,
    ) => {
      setInputError('');
      setSelectedChannel(value);
    },
    [],
  );

  const onTextAreaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInputError('');
      setUserComment(e.target.value);
    },
    [],
  );

  const sendMessage = useCallback(async () => {
    const isValid = !!fields.selectedChannel;
    if (!isValid) {
      setInputError('Please select channel!');
      return;
    }

    const blocks = createSlackMessageBlocks(selectedIssue, {
      user,
      comment: fields.userComment,
    });
    const res = await sendSlackNotification(blocks, {
      issueId: selectedIssue.issueId,
      issueName: selectedIssue.name,
      appId: selectedIssue.app.id,
      appName: selectedIssue.app.name,
      category: selectedIssue.category.name,
      policyId: selectedIssue.policy.id,
      severity: selectedIssue.severity,
    });
    if (res && res.sentSuccessfully && res.timestamp) {
      fields.selectedChannel &&
        pushSlackNotificationToRawIssue(selectedIssue.issueId, {
          channel: fields.selectedChannel.name,
          timestamp: res.timestamp,
        });
      onClose();
    }
  }, [
    fields.selectedChannel,
    fields.userComment,
    onClose,
    selectedIssue,
    user,
  ]);

  const handleClose = useCallback(() => {
    setInputError('');
    onClose();
  }, [onClose]);

  return (
    <ModalDialog
      closeAfterTransition
      closeOnClickOutSide={false}
      sx={{
        '& .MuiPaper-root': {
          minHeight: '31.5vh ',
        },
      }}
      modalFooter={
        <SendSlackMessgeFooter
          onClose={handleClose}
          onSubmit={sendMessage}
          loadingSubmit={loadingSubmit}
          isActionDisabled={loadingSlackChannels}
        />
      }
      onClose={handleClose}
      modalHeader='Send To Slack'
      open={open}>
      <Box justifyContent='flex-start' flexDirection='column' display='flex'>
        {!loadingSlackChannels ? (
          <SendSlackMessgeContent
            error={inputError}
            userComment={fields.userComment}
            selectedChannel={fields.selectedChannel}
            channels={publicChannels}
            onSelectedChannelChange={onSelectedChannelChange}
            onTextAreaChange={onTextAreaChange}
          />
        ) : (
          <AppSpinner text='Loading your public channels ...' />
        )}
      </Box>
    </ModalDialog>
  );
};

export interface SendSlackMessageModalContainerProps {
  open: boolean;
  onClose: () => void;
  selectedIssue: IssueDetails;
}

export default SendSlackMessageModalContainer;
